import { FormErrorsType } from "../types";
import {
  AcknowledgeApplicationResponse,
  Application,
  ApplicationStatus,
  ExpensesType,
  FrequencyTypeUpperCase,
  GetApplicationResponse,
  PersonalDetailsErrorResponse,
  SessionResponse,
  State,
  SubmitApplicationRequest,
  SubmitApplicationResponse,
} from "./types";

type GetMinMonthlyPaymentParams = {
  creditLimit: number | string;
  minMonthlyPaymentPercentage: number;
  minMonthlyPaymentThreshold: number;
};

export function getMinMonthlyPayment({
  creditLimit,
  minMonthlyPaymentPercentage,
  minMonthlyPaymentThreshold,
}: GetMinMonthlyPaymentParams): number {
  const limit = Number(creditLimit);

  return Math.ceil(
    Math.max(
      minMonthlyPaymentThreshold,
      (limit * minMonthlyPaymentPercentage) / 100,
    ),
  );
}

export function getTotalExpenses(
  expenses: ExpensesType,
  errors?: FormErrorsType,
): number | null {
  if (errors && Object.keys(errors).length > 0) {
    return null;
  }

  const amounts = Object.values(expenses).map((expense) => Number(expense));

  if (amounts.some((amount) => isNaN(amount))) {
    return null;
  }

  return amounts.reduce((acc, amount) => acc + amount, 0);
}

export type GetApplicationResponseData = {
  application: Application;
  submitUrl: string;
  acknowledgementUrl: string;
  bankConnectUrl?: string;
  expiryDate?: string;
  latitudePoiUrl?: string;
  cancelUrl?: string;
};

export function parseGetApplicationResponse(
  data: GetApplicationResponse,
): GetApplicationResponseData | null {
  const { application } = data;

  if (!application) {
    console.error("Application is missing is the GET application response.");
    return null;
  }

  let submitUrl;
  if (application.status === ApplicationStatus.CLI_ELIGIBLE) {
    submitUrl = data.links.find(({ rel }) => rel === "submit")?.href;

    if (!submitUrl) {
      console.error("Submit URL is missing is the GET application response.");
      return null;
    }
  }

  let acknowledgementUrl;
  if (
    [
      ApplicationStatus.CLI_IN_PROGRESS,
      ApplicationStatus.CLI_PENDING_ASSESSMENT,
      ApplicationStatus.CLI_STA_PENDING_ACCEPTANCE,
      ApplicationStatus.CLI_CA_PENDING_ACCEPTANCE,
      ApplicationStatus.CLI_PENDING_POI,
      ApplicationStatus.CLI_PENDING_CUST_CALL,
    ].includes(application.status)
  ) {
    acknowledgementUrl = data.links.find(
      ({ rel }) => rel === "acknowledgement",
    )?.href;

    if (!acknowledgementUrl) {
      console.error(
        "Acknowledgement URL is missing in the GET application response.",
      );
      return null;
    }
  }

  let bankConnectUrl;
  let latitudePoiUrl;
  if (application.status === ApplicationStatus.CLI_PENDING_POI) {
    bankConnectUrl = data.links.find(({ rel }) => rel === "poi")?.href;
    if (!bankConnectUrl) {
      console.error(
        "Bank Connect URL is missing in the GET application response.",
      );
      return null;
    }

    latitudePoiUrl = data.links.find(({ rel }) => rel === "poi_latitude")?.href;
    if (!latitudePoiUrl) {
      console.error(
        `POI Latitude url is missing in the GET application response.`,
      );
      return null;
    }
  }

  let cancelUrl;
  if (
    ApplicationStatus.CLI_STA_PENDING_ACCEPTANCE === application.status ||
    ApplicationStatus.CLI_CA_PENDING_ACCEPTANCE === application.status
  ) {
    cancelUrl = data.links.find(({ rel }) => rel === "cancel")?.href;
    if (!cancelUrl) {
      console.error(`Cancel url is missing in the GET application response.`);
      return null;
    }

    acknowledgementUrl = data.links.find(
      ({ rel }) => rel === "acknowledgement",
    )?.href;
    if (!acknowledgementUrl) {
      console.error(
        `Acknowledgement url is missing in the GET application response.`,
      );
      return null;
    }
  }

  if (ApplicationStatus.CLI_PENDING_POI === application.status) {
    cancelUrl = data.links.find(({ rel }) => rel === "cancel")?.href;
    if (!cancelUrl) {
      console.error(`Cancel url is missing in the GET application response.`);
      return null;
    }
  }

  let expiryDate;
  if (
    ![
      ApplicationStatus.INTERNAL_SERVICE_ERROR,
      ApplicationStatus.CLI_NOT_ELIGIBLE,
      ApplicationStatus.CLI_ELIGIBLE,
    ].includes(application.status)
  ) {
    expiryDate = application.expiry_date;

    if (!expiryDate) {
      console.error("Expiry Date is missing is the GET application response.");
      return null;
    }
  }

  return {
    application,
    submitUrl,
    acknowledgementUrl,
    bankConnectUrl,
    expiryDate,
    latitudePoiUrl,
    cancelUrl,
  };
}

type ParsePersonalDetailsErrorResponseReturnType = {
  createSessionUrl: string;
} | null;

export function parsePersonalDetailsErrorResponse(
  data: PersonalDetailsErrorResponse,
): ParsePersonalDetailsErrorResponseReturnType {
  const createSessionUrl = data.links.find(
    ({ rel }) => rel === "sessions",
  )?.href;

  if (!createSessionUrl) {
    console.error(
      "Create session URL is missing is the GET personal details response.",
    );
    return null;
  }

  return {
    createSessionUrl,
  };
}

type ParseSessionResponseReturnType = {
  maskedMobileNumber: string;
  otpVerifyUrl: string;
  otpResendUrl: string;
} | null;

export function parseSessionResponse(
  data: SessionResponse,
): ParseSessionResponseReturnType {
  const maskedMobileNumber = data.mobile_phone;

  if (!maskedMobileNumber) {
    console.error(
      "Masked mobile number is missing is the create session response.",
    );
    return null;
  }

  const otpVerifyUrl = data.links.find(({ rel }) => rel === "verify")?.href;

  if (!otpVerifyUrl) {
    console.error("Verify OTP URL is missing is the create session response.");
    return null;
  }

  const otpResendUrl = data.links.find(({ rel }) => rel === "resend")?.href;

  if (!otpResendUrl) {
    console.error("Resend OTP URL is missing is the create session response.");
    return null;
  }

  return {
    maskedMobileNumber,
    otpVerifyUrl,
    otpResendUrl,
  };
}

export type ParseSubmitApplicationResponseReturnType = {
  application: Application;
  acknowledgementUrl: string;
  bankConnectUrl: string;
  cancelUrl: string;
  latitudePoiUrl?: string;
} | null;

export function parseSubmitApplicationResponse(
  data: SubmitApplicationResponse,
): ParseSubmitApplicationResponseReturnType {
  const { application } = data;

  if (!application) {
    console.error("Application is missing in the submit application response.");
    return null;
  }

  let acknowledgementUrl;

  if (application.status !== ApplicationStatus.INTERNAL_SERVICE_ERROR) {
    acknowledgementUrl = data.links.find(
      ({ rel }) => rel === "acknowledgement",
    )?.href;

    if (!acknowledgementUrl) {
      console.error(
        "Acknowledgement URL is missing in the submit application response.",
      );
      return null;
    }
  }

  const bankConnectUrl = data.links.find(({ rel }) => rel === "poi")?.href;

  if (!bankConnectUrl) {
    console.error(
      "Bank Connect URL is missing in the submit application response.",
    );
    return null;
  }

  const latitudePoiUrl = data.links.find(
    ({ rel }) => rel === "poi_latitude",
  )?.href;
  if (!latitudePoiUrl) {
    console.error(
      `POI Latitude url is missing in the GET application response.`,
    );
  }
  const cancelUrl = data.links.find(({ rel }) => rel === "cancel")?.href;

  if (!cancelUrl) {
    console.error("Cancel URL is missing in the submit application response.");
    return null;
  }

  return {
    application,
    acknowledgementUrl,
    bankConnectUrl,
    cancelUrl,
    latitudePoiUrl,
  };
}

type ParseAcknowledgementResponseReturnType = {
  application: Application;
  bankConnectUrl: string;
  latitudePoiUrl: string;
  cancelUrl?: string;
};

export function parseAcknowledgeApplicationResponse(
  data: AcknowledgeApplicationResponse,
): ParseAcknowledgementResponseReturnType | null {
  const { application, links } = data;

  if (!application) {
    console.error(
      "Application is missing is the acknowledge application response.",
    );
    return null;
  }
  const bankConnectUrl = links.find(({ rel }) => rel === "poi")?.href;
  if (!bankConnectUrl) {
    console.error(
      "Bank Connect Url link is missing in the acknowledge application response",
    );
    return null;
  }

  const latitudePoiUrl = links.find(({ rel }) => rel === "poi_latitude")?.href;
  if (!latitudePoiUrl) {
    console.error(
      "Latitude POI Link is missing in the acknowledge application response",
    );
    return null;
  }

  let cancelUrl;
  if (application.status === ApplicationStatus.CLI_PENDING_POI) {
    cancelUrl = links.find(({ rel }) => rel === "cancel")?.href;
    if (!cancelUrl) {
      console.error(
        "Cancel url is missing in the acknowledge application response",
      );
      return null;
    }
  }

  return {
    application,
    bankConnectUrl,
    latitudePoiUrl,
    cancelUrl,
  };
}

export const getApplicationPayload = (
  state: State,
  values: { iAgree: boolean },
): SubmitApplicationRequest => ({
  privacy_policy_acknowledgement: values.iAgree,
  application: {
    requested_credit_limit: Number(state.newCreditLimit) * 100,
    personal_information: {
      relationship_status: state.personalInformation.relationshipStatus,
      number_of_dependants: Number(
        state.personalInformation.numberOfDependants,
      ),
    },
    residential_address: {
      residential_status: state.residentialAddress.residentialStatus,
      mortgage_or_rent: Number(state.residentialAddress.mortgageOrRent) * 100,
      time_at_current_address_year: Number(
        state.residentialAddress.timeAtCurrentAddress.years,
      ),
      time_at_current_address_month: Number(
        state.residentialAddress.timeAtCurrentAddress.months,
      ),
    },
    employment_details: {
      employment_status: state.employmentDetails.employmentStatus,
      occupation: state.employmentDetails.occupation,
      time_with_current_employer_year: Number(
        state.employmentDetails.timeWithCurrentEmployer.years,
      ),
      time_with_current_employer_month: Number(
        state.employmentDetails.timeWithCurrentEmployer.months,
      ),
    },
    income: {
      net_income: Number(state.income.netIncome.amount) * 100,
      net_income_frequency:
        state.income.netIncome.frequency.toUpperCase() as FrequencyTypeUpperCase,
      other_income: Number(state.income.otherIncome.amount) * 100,
      other_income_frequency:
        state.income.otherIncome.frequency.toUpperCase() as FrequencyTypeUpperCase,
    },
    monthly_expenses: {
      living_expenses_shared:
        state.monthlyExpenses.shareLivingExpenses === "yes",
      education_childcare:
        Number(state.monthlyExpenses.expenses.education) * 100,
      transport: Number(state.monthlyExpenses.expenses.transport) * 100,
      insurances: Number(state.monthlyExpenses.expenses.insurances) * 100,
      utilities: Number(state.monthlyExpenses.expenses.utilities) * 100,
      personal_expenses: Number(state.monthlyExpenses.expenses.personal) * 100,
      lifestyle: Number(state.monthlyExpenses.expenses.lifestyle) * 100,
    },
    other_cards_loans: {
      loan_repayments: Number(state.additionalDebts.loanRepayments) * 100,
      total_credit_limit: Number(state.additionalDebts.totalCreditLimit) * 100,
      total_amounts_owing: Number(state.additionalDebts.totalOwing) * 100,
    },
  },
});
